import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { avatarSrc, formatApiError } from "../lib/api";
import { BadgeCheck, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const VerificationQueue = ({ onDecided }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  useEffect(() => { api.get("/admin/verification-requests").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);

  const decide = async (r, approve) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/verification-requests/${r.id}/decision`, { approve, note: approve ? "" : (notes[r.id] || "") });
      setRows((list) => list.filter((x) => x.id !== r.id));
      toast.success(approve ? `@${r.username} is now verified` : `Declined @${r.username}`);
      onDecided?.(r.user_id, approve);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (!rows || rows.length === 0) return null;
  return (
    <section data-testid="verification-queue" className="mb-6 border border-[#0066FF]/30 bg-[#0066FF]/[0.05] rounded-2xl p-4">
      <h3 className="flex items-center gap-2 font-heading text-white text-sm uppercase tracking-wider mb-3">
        <BadgeCheck size={16} className="text-white" fill="#0066FF" /> Verification requests
        <span data-testid="verification-queue-count" className="ml-1 bg-[#0066FF] text-white text-[10px] font-bold rounded-full px-2 py-0.5">{rows.length}</span>
      </h3>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} data-testid={`vreq-${r.username}`} className="flex flex-col sm:flex-row sm:items-start gap-3 bg-[#0A0A0C] border border-white/[0.06] rounded-xl p-3">
            <Link to={`/u/${r.username}`} className="flex items-center gap-2.5 sm:w-52 shrink-0 min-w-0">
              {r.avatar_url ? <img src={avatarSrc(r.avatar_url)} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-[#0066FF] flex items-center justify-center text-white text-sm font-semibold">{r.display_name?.[0]?.toUpperCase()}</div>}
              <span className="min-w-0 leading-tight">
                <span className="block text-white text-sm font-semibold truncate">{r.display_name}</span>
                <span className="block text-[#8A8D91] text-xs truncate">@{r.username} · {r.media_count ?? 0} posts</span>
              </span>
            </Link>
            <div className="flex-1 min-w-0 text-sm text-[#D1D5DB] whitespace-pre-wrap break-words">
              {r.reason}
              {r.links && <p className="mt-1 text-xs text-[#4599FF] break-all">{r.links}</p>}
              <input data-testid={`vreq-note-${r.username}`} value={notes[r.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} maxLength={500} placeholder="Note if declining (optional, shown to member)" className="mt-2 w-full bg-[#141414] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#0066FF]" />
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`vreq-approve-${r.username}`} disabled={busy === r.id} onClick={() => decide(r, true)} className="flex items-center gap-1 bg-[#0066FF] hover:bg-[#0055CC] text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50">{busy === r.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve</button>
              <button data-testid={`vreq-deny-${r.username}`} disabled={busy === r.id} onClick={() => decide(r, false)} className="flex items-center gap-1 border border-white/10 text-[#9CA3AF] hover:text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50"><X size={13} /> Decline</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
